import React from 'react';
import { Tooltip } from 'shared/components/Tooltip';
import './style.less';
import { CallTask, CompletedCallTaskStates } from 'types/outbound';
import { Icon } from 'shared/components/Icon';
import EmptyAvatar from 'shared/components/EmptyAvatar';
import { Button } from 'shared/components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveOrgIntegrations } from 'shared/store/auth-slice';
import clsx from 'clsx';
import { setDetailTask } from 'shared/store/outbound-slice';
import {
  CallServiceName,
  getExternalContactUrl,
  getExternalServiceIconName,
} from 'browser/components/Outbound/helpers';

interface Props {
  task: CallTask;
  onSingleDialClick: (task: CallTask) => Promise<void>;
  onStop: () => Promise<void>;
  isDialing?: boolean;
}

const CallTaskTooltipExternalServiceButton: React.FC<{
  task: CallTask;
  serviceName: CallServiceName;
  text: string;
  target?: string;
}> = ({ task, serviceName, text }) => {
  const orgIntegrations = useSelector(selectActiveOrgIntegrations);
  const url = getExternalContactUrl(task, serviceName, orgIntegrations);
  const icon = getExternalServiceIconName(serviceName);

  if (url === null) {
    return null;
  }

  return (
    <Button
      href={url}
      target="_blank"
      className="CallTaskTooltip__actions-button CallTaskTooltip__actions-button--icon"
    >
      <span>
        <Icon icon={icon} />
        {text}
      </span>
      <Icon icon="arrowTopRight" />
    </Button>
  );
};

const CallTaskTooltipCard: React.FC<Props> = ({
  task,
  onSingleDialClick,
  onStop,
  isDialing,
}) => {
  const dispatch = useDispatch();
  const { firstName, lastName, company, email } = task.contact;
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();

  return (
    <div className="CallTaskTooltip" onClick={e => e.stopPropagation()}>
      <div className="CallTaskTooltip__top">
        <div className="CallTaskTooltip__top-details">
          <div className="CallTaskTooltip__title">{!name ? email : name}</div>
          {company && (
            <div className="CallTaskTooltip__company">
              <Icon icon="companies2" />
              {company?.name}
            </div>
          )}
        </div>
        <div className="CallTaskTooltip__top-avatar">
          <EmptyAvatar name={name} size={48} id={task.contact.id} />
        </div>
      </div>
      {!CompletedCallTaskStates.includes(task.status) && (
        <Button
          className={clsx(
            `CallTaskTooltip__call`,
            isDialing && `CallTaskTooltip__call--cancel`,
          )}
          type="primary"
          size="middle"
          onClick={async () => await (isDialing ? onStop() : onSingleDialClick(task))}
        >
          <Icon icon="phoneOn" />
          {!isDialing ? 'Call' : 'Cancel'}
        </Button>
      )}
      <div className="CallTaskTooltip__actions">
        <Button
          className="CallTaskTooltip__actions-button CallTaskTooltip__actions-button--icon"
          onClick={() => dispatch(setDetailTask(task))}
        >
          Open profile
          <Icon icon="arrowRightCircle" />
        </Button>
        {task.sourceType?.includes('hubspot_') && (
          <CallTaskTooltipExternalServiceButton
            task={task}
            serviceName={'hubspot'}
            text={'HubSpot'}
          />
        )}
        {task.sourceType?.includes('outreach_') && (
          <CallTaskTooltipExternalServiceButton
            task={task}
            serviceName={'outreach'}
            text={'Outreach'}
          />
        )}
        {task.sourceType?.includes('apollo_') && (
          <CallTaskTooltipExternalServiceButton
            task={task}
            serviceName={'apollo'}
            text={'Apollo'}
          />
        )}
        {task.sourceType?.includes('salesloft_') && (
          <CallTaskTooltipExternalServiceButton
            task={task}
            serviceName={'salesloft'}
            text={'Salesloft'}
          />
        )}
        {task.sourceType?.includes('salesforce_') && (
          <CallTaskTooltipExternalServiceButton
            task={task}
            serviceName={'salesforce'}
            text={'Salesforce'}
          />
        )}
        <CallTaskTooltipExternalServiceButton
          task={task}
          serviceName={'linkedin'}
          text={'Linkedin'}
        />
        <CallTaskTooltipExternalServiceButton
          task={task}
          serviceName={'google'}
          text={'Google'}
        />
      </div>
    </div>
  );
};

export const CallTaskTooltip: React.FC<Props> = ({ children, ...props }) => {
  return (
    <Tooltip
      destroyTooltipOnHide
      overlayClassName="CallTaskTooltip__wrap"
      theme="light"
      title={<CallTaskTooltipCard {...props} />}
      mouseEnterDelay={0.3}
      mouseLeaveDelay={0.15}
    >
      {children}
    </Tooltip>
  );
};
